import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Region-level state card for "the thing that lives here can't be
 * shown right now". Covers both load failures (`variant: 'error'`)
 * and legitimate empty data (`variant: 'empty'`). One component with
 * two intents keeps the visual language consistent — a failed list
 * and an empty list both occupy the same space, and the only thing
 * the user needs to notice is whether there's a retry action.
 *
 * The parent owns the retry handler; this component only emits the
 * event so the caller can re-fetch, re-run a query, etc.
 */
@Component({
  selector: 'lib-empty-state',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './empty-state.html',
  styleUrls: ['./empty-state.scss'],
})
export class EmptyStateComponent {
  readonly variant = input<'empty' | 'error'>('empty');
  /** PrimeIcons glyph (without the `pi-` prefix). Defaults by variant. */
  readonly icon = input<string | null>(null);
  readonly title = input.required<string>();
  readonly message = input<string | null>(null);
  /** When provided, an action button is rendered below the message. */
  readonly actionLabel = input<string | null>(null);

  @Output() readonly action = new EventEmitter<void>();

  readonly iconClass = computed(() => {
    const explicit = this.icon();
    if (explicit) return `pi pi-${explicit}`;
    return this.variant() === 'error'
      ? 'pi pi-exclamation-triangle'
      : 'pi pi-inbox';
  });
}
